import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import { hasClassicCounterpart } from "@ugg/shared/utils/multi-build-helpers";
import { SPECIAL_GAME_MODES } from "@ugg/shared/utils/special-game-modes";
import {
  getChampionBuildUrl,
  getClassicChampionBuildUrl,
  getSpecialGameModeChampionBuildUrl,
} from "@ugg/shared/routes/app-routes";

// ─────────────────────────────────────────────────────────────────────
// Resolve the right champion build page for a given queue.
//
// Three URL grammars exist and the choice depends on the queue:
//   • League Classic  /lol/champions/classic/<alias>-classic/<role?>
//   • special modes   /lol/champions/<mode>/<alias>-<mode>
//   • everything else /lol/champions/<alias>/build/<role?>
//
// Classic can't be folded into SPECIAL_GAME_MODES — that map is keyed by
// QueueTypeS and assumes the `<mode>/<alias>-<mode>` grammar plus the
// standard build pipeline, neither of which Classic uses.
//
// This lives in its own module rather than app-routes.ts because
// app-routes is deliberately dependency-light and special-game-modes
// imports *from* it; putting the resolver there would create a cycle.
// ─────────────────────────────────────────────────────────────────────

interface ChampionBuildUrlParams {
  language?: string;
  role?: string;
  // Live u.gg patch, forwarded to hasClassicCounterpart so pre-deployed roster
  // additions resolve here too (see CLASSIC_COUNTERPART_ALIASES_BY_PATCH).
  // Optional because the main caller, ChampLink, is rendered once per champion
  // card in a live game / champ select and has no patch in scope — adding a
  // fetch there to serve a handful of pending aliases isn't worth it. Without
  // it this resolves the core roster only, so a champion in its pre-activation
  // window links to the modern build page instead of the Classic one. That is a
  // soft miss, not a broken link, and it disappears once the aliases are folded
  // into CLASSIC_COUNTERPART_ALIASES.
  patch?: string;
}

export function getChampionBuildUrlForQueue(championAlias: string, queueType?: string, params?: ChampionBuildUrlParams): string {
  const { language, role, patch } = params || {};

  // ARAM Mayhem Classic (Riot's KIWI_JADE) is a MAYHEM game, not a League Classic
  // one. It runs the modern champion roster and modern kits on a Classic-themed
  // map with Classic-themed items and augments — so the Classic build page, which
  // is built around the Classic champion kits, is the wrong destination. The
  // Mayhem page is right, and it carries the augment module these games have.
  //
  // It has no SPECIAL_GAME_MODES entry of its own, so fold it onto ARAM_MAYHEM
  // before the lookup below; without that it would fall through to the standard
  // build page instead.
  const effectiveQueueType = queueType === QueueTypeS.ARAM_MAYHEM_CLASSIC ? QueueTypeS.ARAM_MAYHEM : queueType;

  // League Classic proper — the only queue whose champions really are the Classic
  // kits, hence the separate URL grammar.
  if (effectiveQueueType === QueueTypeS.CLASSIC && hasClassicCounterpart(championAlias, patch)) {
    return getClassicChampionBuildUrl(championAlias, { language, role });
  }

  const { urlName } = Object.values(SPECIAL_GAME_MODES).find((mode) => mode.queueType === effectiveQueueType) || {};
  if (urlName) {
    return getSpecialGameModeChampionBuildUrl(championAlias, urlName, { language });
  }

  return getChampionBuildUrl(championAlias, { language, ...(role && { role }) });
}
